import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Container, Grid, Paper, Typography } from '@mui/material';
import { useQuery, useQueryHistory, useDocumentSummary } from '../../../hooks/useDocs';
import { DEFAULT_TOP_K } from '../constants';
import { APP_PAGES } from '../../../pages';
import { getQueryResultCache, saveQueryResultCache } from '../../../Utils/queryResultCache';
import QueryInput from './QueryInput';
import AnswerCard from './AnswerCard';
import RankedResultsList from './RankedResultsList';
import SourcesList from './SourcesList';
import QueryUsageFooter from './QueryUsageFooter';
import QueryHistory from './QueryHistory';
import QueryLoadingOverlay from './QueryLoadingOverlay';
import QueryStatsCards from './QueryStatsCards';
import TagSections from '../../../Components/TagSections';
import { useCustomerTags } from '../../../hooks/useCustomerTags';
import { buildDocsQueryUrl, DOCS_APP_BASE_URL } from './queryLoadingSteps';

const Query: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialQuery = searchParams.get('q') || '';
  const hasRestoredRef = useRef(false);

  const [query, setQuery] = useState(initialQuery);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const {
    submitQuery,
    result: queryResult,
    isLoading: isQueryLoading,
    error: queryError,
    restoreResult,
    reset: resetQueryResult,
  } = useQuery();
  const { addToHistory } = useQueryHistory();
  const { summary: stats, isLoading: statsLoading } = useDocumentSummary();
  const {
    techTags,
    businessTags,
    techTagsLoading,
    businessTagsLoading,
    techTagsError,
    businessTagsError,
  } = useCustomerTags();

  const syncSearchParams = useCallback(
    (q: string, replace = false) => {
      setSearchParams({ q }, { replace });
    },
    [setSearchParams]
  );

  const navigateToDocsQuery = useCallback(
    (queryText: string): boolean => {
      try {
        const targetOrigin = new URL(DOCS_APP_BASE_URL).origin;
        if (window.location.origin !== targetOrigin) {
          window.location.href = buildDocsQueryUrl(queryText);
          return true;
        }
      } catch {
        // Invalid base URL — stay on the current page
      }
      syncSearchParams(queryText, true);
      return false;
    },
    [syncSearchParams]
  );

  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery);
    }
  }, [initialQuery]);

  useEffect(() => {
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    const cached = getQueryResultCache();
    const urlQuery = searchParams.get('q')?.trim() || '';

    if (cached) {
      const cacheMatchesUrl =
        !urlQuery ||
        (cached.query === urlQuery && cached.topK === DEFAULT_TOP_K);

      if (cacheMatchesUrl) {
        setQuery(cached.query);
        restoreResult(cached.result);
        syncSearchParams(urlQuery || cached.query, true);
        return;
      }
    }

    if (urlQuery) {
      syncSearchParams(urlQuery, true);
      submitQuery({ query: urlQuery, topK: DEFAULT_TOP_K })
        .then((response) => {
          saveQueryResultCache({
            query: urlQuery,
            topK: DEFAULT_TOP_K,
            result: response,
          });
          addToHistory({
            query: urlQuery,
            answerPreview: response.answer.substring(0, 100),
          });
        })
        .catch(() => {
          // Error handled by hook
        });
    }
    // Run once on mount to restore cached results or re-run URL-driven queries
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runQuery = async (queryText: string) => {
    const trimmedQuery = queryText.trim();
    if (!trimmedQuery) return;

    setQuery(trimmedQuery);
    resetQueryResult();

    const didRedirect = navigateToDocsQuery(trimmedQuery);
    if (didRedirect) {
      return;
    }

    try {
      const response = await submitQuery({ query: trimmedQuery, topK: DEFAULT_TOP_K });
      saveQueryResultCache({
        query: trimmedQuery,
        topK: DEFAULT_TOP_K,
        result: response,
      });
      addToHistory({
        query: trimmedQuery,
        answerPreview: response.answer.substring(0, 100),
      });
    } catch {
      // Error handled by hook
    }
  };

  const handleSubmit = async () => {
    await runQuery(query);
  };

  const handleTagClick = (tagName: string) => {
    setSelectedTags((prev) => {
      const isSelected = prev.some((t) => t.toLowerCase() === tagName.toLowerCase());
      const next = isSelected
        ? prev.filter((t) => t.toLowerCase() !== tagName.toLowerCase())
        : [...prev, tagName];
      setQuery(next.join(', '));
      return next;
    });
  };

  const isLoading = isQueryLoading;
  const error = queryError;
  const activeQuery = query.trim();

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#F8F8F8', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        <QueryStatsCards
          stats={stats}
          isLoading={statsLoading}
          onLibraryClick={() => navigate(APP_PAGES.DOCS_LIBRARY)}
        />

        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12} md={9}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <QueryInput
                value={query}
                onChange={setQuery}
                onSubmit={handleSubmit}
                isLoading={isLoading}
                data-testid="docs-query-input"
              />
            </Paper>

            {isLoading && activeQuery && (
              <QueryLoadingOverlay query={activeQuery} data-testid="docs-query-loading-overlay" />
            )}

            {/* Results */}
            {error && (
              <Paper sx={{ p: 3, mb: 3, bgcolor: '#FFEBEE' }}>
                <Typography color="error">{error}</Typography>
              </Paper>
            )}

            {!isLoading && queryResult && (
              <>
                <AnswerCard answer={queryResult.answer} data-testid="docs-answer-card" />
                {queryResult.usage && (
                  <QueryUsageFooter usage={queryResult.usage} data-testid="docs-query-usage" />
                )}
                {queryResult.rankedResults.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <RankedResultsList
                      results={queryResult.rankedResults}
                      data-testid="docs-ranked-results"
                    />
                  </Box>
                )}
                {queryResult.references.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <SourcesList
                      references={queryResult.references}
                      data-testid="docs-references-list"
                    />
                  </Box>
                )}
              </>
            )}

            {!isLoading && !error && !queryResult && query && (
              <Paper sx={{ p: 3 }}>
                <Typography color="text.secondary" textAlign="center">
                  Enter a query above to search your documents
                </Typography>
              </Paper>
            )}
          </Grid>

          {/* Sidebar - Tags & query history */}
          <Grid item xs={12} md={3}>
            <Box
              sx={{
                position: 'sticky',
                top: 20,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <TagSections
                businessTags={businessTags}
                techTags={techTags}
                isBusinessTagsLoading={businessTagsLoading}
                isTechTagsLoading={techTagsLoading}
                businessTagsError={businessTagsError}
                techTagsError={techTagsError}
                selectedTags={selectedTags}
                onTagClick={(tagName) => handleTagClick(tagName)}
                layout="sidebar"
              />
              <QueryHistory
                onSelectQuery={(q) => {
                  setQuery(q);
                  setSelectedTags([]);
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Query;
